import { offsetProfileCurveLoop } from './offsetProfileCurveLoop';
import type { PlanarLoopOffsetPlane } from './offsetPlanarLoop';
import {
  canOffsetCompositeProfileCurveLoop,
  sampleCompositeProfileCurveLoopPoints,
} from './offsetProfileCurveSegment';

/**
 * 区域整体偏置模式。
 */
export type ProfileRegionOffsetMode = 'outward' | 'inward';

const MIN_REGION_OFFSET = 1e-8;

/**
 * 克隆三维点集合。
 *
 * @param points 原始点集合。
 * @returns 克隆后的点集合。
 */
function clonePointList(points: Array<[number, number, number]>): Array<[number, number, number]> {
  return points.map((point) => [point[0], point[1], point[2]]);
}

/**
 * 克隆区域几何。
 *
 * @param region 原始区域。
 * @returns 深拷贝后的区域。
 */
function cloneRegionGeometry(region: ProfileRegionGeometry): ProfileRegionGeometry {
  return {
    ...region,
    outer: clonePointList(region.outer),
    outerSegments: region.outerSegments?.map((segment) => structuredClone(segment)),
    holes: region.holes?.map((hole) => clonePointList(hole)) ?? [],
    holeSegments: region.holeSegments?.map((segments) => segments.map((segment) => structuredClone(segment))),
  };
}

/**
 * 构建局部平面描述。
 *
 * @param region 目标区域。
 * @returns 局部平面。
 */
function buildRegionPlane(region: ProfileRegionGeometry): PlanarLoopOffsetPlane {
  return {
    origin: region.outer[0] ?? [0, 0, 0],
    xAxis: region.xAxis,
    yAxis: region.yAxis,
  };
}

/**
 * 归一化孔洞曲线段输出。
 *
 * @param holeResults 孔洞偏置结果。
 * @returns 孔洞曲线段；全部缺失时返回 undefined。
 */
function resolveHoleSegments(
  holeResults: Array<{ segments: ProfileCurveSegment[] | undefined }>,
): ProfileCurveSegment[][] | undefined {
  if (holeResults.every((result) => !result.segments || result.segments.length <= 0)) {
    return undefined;
  }
  return holeResults.map((result) => result.segments ?? []);
}

/**
 * 用可闭合组合曲线段重采样 outer，使点列与 outerSegments / Puppy CAD 内核 构面语义一致。
 *
 * @param region 区域几何。
 * @returns 同步后的区域几何。
 */
export function resyncProfileRegionGeometryOuterFromSegments(
  region: ProfileRegionGeometry,
): ProfileRegionGeometry {
  const outerSegments = region.outerSegments;
  if (!outerSegments || outerSegments.length <= 0 || !canOffsetCompositeProfileCurveLoop(outerSegments)) {
    return region;
  }
  return {
    ...region,
    outer: sampleCompositeProfileCurveLoopPoints(outerSegments).map(
      (point) => [point[0], point[1], point[2]] as [number, number, number],
    ),
  };
}

/**
 * 偏置整个区域。
 *
 * @param region 原始区域。
 * @param mode 偏置方向。
 * @param distance 偏置距离。
 * @returns 偏置后的区域。
 */
export function offsetProfileRegionGeometry(
  region: ProfileRegionGeometry,
  mode: ProfileRegionOffsetMode,
  distance: number,
): ProfileRegionGeometry {
  if (distance <= MIN_REGION_OFFSET) {
    return cloneRegionGeometry(region);
  }

  const plane = buildRegionPlane(region);
  const holeMode: ProfileRegionOffsetMode = mode === 'outward' ? 'inward' : 'outward';
  const outerResult = offsetProfileCurveLoop(
    region.outer,
    region.outerSegments,
    plane,
    mode,
    distance,
  );
  const holeResults = (region.holes ?? []).map((hole, index) => (
    offsetProfileCurveLoop(
      hole,
      region.holeSegments?.[index],
      plane,
      holeMode,
      distance,
    )
  ));

  return {
    ...region,
    outer: outerResult.points,
    outerSegments: outerResult.segments,
    holes: holeResults.map((result) => result.points),
    holeSegments: resolveHoleSegments(holeResults),
  };
}
